import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";
import { colors } from "../../../ui/colors";
import { ModalState } from "./modal-types";
import { TEMPLATES } from "../../templates/loader";
import { Template } from "../../templates/types/templates.types";

interface DashboardModalsHostProps {
  modalState: ModalState;
  onClose: () => void;
  isSubmiting: boolean;
  onConfirm: (data?: any) => void;
}

const modalStyles = {
  paper: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: "12px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    padding: 3,
    minWidth: 400,
    maxWidth: 500,
    outline: "none",
  },
  largeModal: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "1100px",
    maxHeight: "85vh",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: "16px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    padding: 0,
    outline: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
  },
  modalHeader: {
    padding: "24px 32px",
    borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalContent: {
    padding: "32px",
    overflowY: "auto" as const,
    flex: 1,
  },
  modalFooter: {
    padding: "20px 32px",
    borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
  },
  backdrop: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(4px)",
  },
};

const Spinner = () => <CircularProgress size={16} sx={{ color: "inherit" }} />;

export const DashboardModalsHost: React.FC<DashboardModalsHostProps> = ({
  modalState,
  onClose,
  isSubmiting,
  onConfirm,
}) => {
  const [inputValue, setInputValue] = React.useState(modalState.projectName || "");
  const [selectedTemplate, setSelectedTemplate] = React.useState<Template | null>(null);

  React.useEffect(() => {
    setInputValue(modalState.projectName || "");
  }, [modalState.projectName]);

  React.useEffect(() => {
    if (modalState.type === "chooseTemplate" && modalState.isOpen) {
      setSelectedTemplate(null);
    }
  }, [modalState.type, modalState.isOpen]);

  const handleTemplateClick = (template: Template) => {
    if (isSubmiting) return;
    setSelectedTemplate(template);
  };

  const handleConfirmTemplate = () => {
    if (!selectedTemplate) return;
    onConfirm({ template: selectedTemplate });
    onClose();
  };

  const safeClose = () => {
    if (isSubmiting) return;
    onClose();
  };

  const modalOnClose = (_: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
    if (isSubmiting && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    onClose();
  };

  return (
    <>
      {/* Rename Modal */}
      {modalState.type === "rename" && (
        <Modal open={modalState.isOpen} onClose={modalOnClose}>
          <Box sx={modalStyles.paper}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "var(--fx-surface-text, #0F172A)", fontSize: "18px" }}>
              Rename Project
            </Typography>

            <TextField
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter new project name"
              size="small"
              autoFocus
              disabled={isSubmiting}
              sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />

            <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={safeClose}
                disabled={isSubmiting}
                sx={{
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "var(--fx-surface-sunken, #F8FAFC)", borderColor: "var(--fx-surface-texttertiary, #94A3B8)" },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={() => onConfirm({ name: inputValue, id: modalState.projectId })}
                disabled={isSubmiting || !inputValue.trim()}
                startIcon={isSubmiting ? <Spinner /> : undefined}
                sx={{
                  backgroundColor: colors.accent || "#ff9500",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 24px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: colors.accentHover || "#e68900",
                    boxShadow: "0 4px 8px rgba(255, 149, 0, 0.2)",
                  },
                }}
              >
                {isSubmiting ? "Renaming..." : "Rename"}
              </Button>
            </Stack>
          </Box>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {modalState.type === "delete" && (
        <Modal open={modalState.isOpen} onClose={modalOnClose}>
          <Box sx={modalStyles.paper}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "var(--fx-surface-text, #0F172A)", fontSize: "18px" }}>
              Delete "{modalState.projectName}"?
            </Typography>

            <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", mb: 3, fontSize: "14px", lineHeight: 1.6 }}>
              This action cannot be undone. The project and all its data will be permanently deleted.
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={safeClose}
                disabled={isSubmiting}
                sx={{
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "var(--fx-surface-sunken, #F8FAFC)", borderColor: "var(--fx-surface-texttertiary, #94A3B8)" },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={() => onConfirm({ id: modalState.projectId })}
                disabled={isSubmiting}
                startIcon={isSubmiting ? <Spinner /> : undefined}
                sx={{
                  backgroundColor: "#ef4444",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 24px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#dc2626", boxShadow: "0 4px 8px rgba(239, 68, 68, 0.2)" },
                }}
              >
                {isSubmiting ? "Deleting..." : "Delete Project"}
              </Button>
            </Stack>
          </Box>
        </Modal>
      )}

      {/* Duplicate Modal */}
      {modalState.type === "duplicate" && (
        <Modal open={modalState.isOpen} onClose={modalOnClose}>
          <Box sx={modalStyles.paper}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "var(--fx-surface-text, #0F172A)", fontSize: "18px" }}>
              Duplicate Project
            </Typography>

            <TextField
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter name for duplicated project"
              size="small"
              autoFocus
              disabled={isSubmiting}
              sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />

            <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={safeClose}
                disabled={isSubmiting}
                sx={{
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "var(--fx-surface-sunken, #F8FAFC)", borderColor: "var(--fx-surface-texttertiary, #94A3B8)" },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={() => onConfirm({ name: inputValue })}
                disabled={isSubmiting || !inputValue.trim()}
                startIcon={isSubmiting ? <Spinner /> : undefined}
                sx={{
                  backgroundColor: colors.accent || "#ff9500",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 24px",
                  borderRadius: "8px",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: colors.accentHover || "#e68900",
                    boxShadow: "0 4px 8px rgba(255, 149, 0, 0.2)",
                  },
                }}
              >
                {isSubmiting ? "Duplicating..." : "Duplicate"}
              </Button>
            </Stack>
          </Box>
        </Modal>
      )}

      {/* Template Chooser Modal */}
      {modalState.type === "chooseTemplate" && (
        <Modal
          open={modalState.isOpen}
          onClose={modalOnClose}
          slotProps={{ backdrop: { sx: modalStyles.backdrop } }}
        >
          <Box sx={modalStyles.largeModal}>
            {/* Header */}
            <Box sx={modalStyles.modalHeader}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--fx-surface-text, #0F172A)", fontSize: "20px", mb: "4px" }}>
                  Choose a Template
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: "14px" }}>
                  Select a pre-built template to start your project
                </Typography>
              </Box>

              <IconButton onClick={safeClose} disabled={isSubmiting} sx={{ color: "var(--fx-surface-textsecondary, #64748B)" }}>
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={modalStyles.modalContent}>
              <Grid container spacing={3}>
                {TEMPLATES.map((template: Template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card
                      onClick={() => handleTemplateClick(template)}
                      sx={{
                        cursor: isSubmiting ? "not-allowed" : "pointer",
                        opacity: isSubmiting ? 0.7 : 1,
                        border: selectedTemplate?.id === template.id ? "2px solid #ff9500" : "1px solid var(--fx-surface-border, #E2E8F0)",
                        borderRadius: "12px",
                        transition: "all 0.2s ease",
                        position: "relative",
                        overflow: "hidden",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "none",
                        "&:hover": isSubmiting
                          ? undefined
                          : {
                            borderColor: selectedTemplate?.id === template.id ? "#ff9500" : "var(--fx-surface-borderstrong, #CBD5E1)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                            transform: "translateY(-2px)",
                          },
                      }}
                    >
                      <Box sx={{ position: "relative", paddingTop: "60%", backgroundColor: "var(--fx-surface-muted, #F1F5F9)", overflow: "hidden" }}>
                        {template.thumbnailUrl ? (
                          <CardMedia
                            component="img"
                            image={template.thumbnailUrl}
                            alt={template.name}
                            sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            }}
                          >
                            <Typography sx={{ color: "#ffffff", fontSize: "14px", fontWeight: 500 }}>No preview</Typography>
                          </Box>
                        )}

                        {selectedTemplate?.id === template.id && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              backgroundColor: "#ff9500",
                              borderRadius: "50%",
                              padding: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CheckCircle sx={{ color: "#ffffff", fontSize: "20px" }} />
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ padding: "16px", flex: 1, display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--fx-surface-text, #0F172A)", fontSize: "15px", mb: "6px", lineHeight: 1.4 }}>
                          {template.name}
                        </Typography>

                        <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: "13px", lineHeight: 1.5, mb: "12px", flex: 1 }}>
                          {template.description}
                        </Typography>

                        {template.blocks && template.blocks.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {template.blocks.slice(0, 3).map((block, index) => (
                              <Chip
                                key={index}
                                label={block}
                                size="small"
                                sx={{
                                  backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                                  color: "var(--fx-surface-textsecondary, #64748B)",
                                  fontSize: "11px",
                                  height: "22px",
                                  fontWeight: 500,
                                  "& .MuiChip-label": { padding: "0 8px" },
                                }}
                              />
                            ))}
                            {template.blocks.length > 3 && (
                              <Chip
                                label={`+${template.blocks.length - 3}`}
                                size="small"
                                sx={{
                                  backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                                  color: "var(--fx-surface-textsecondary, #64748B)",
                                  fontSize: "11px",
                                  height: "22px",
                                  fontWeight: 500,
                                  "& .MuiChip-label": { padding: "0 8px" },
                                }}
                              />
                            )}
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {TEMPLATES.length === 0 && (
                <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
                  <Typography variant="h6" sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: "16px", fontWeight: 500 }}>
                    No templates available
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={modalStyles.modalFooter}>
              <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={safeClose}
                  disabled={isSubmiting}
                  sx={{
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                    textTransform: "none",
                    fontSize: "14px",
                    padding: "8px 20px",
                    borderRadius: "8px",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "var(--fx-surface-sunken, #F8FAFC)", borderColor: "var(--fx-surface-texttertiary, #94A3B8)" },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleConfirmTemplate}
                  disabled={isSubmiting || !selectedTemplate}
                  startIcon={isSubmiting ? <Spinner /> : undefined}
                  sx={{
                    backgroundColor: "#ff9500",
                    color: "#ffffff",
                    textTransform: "none",
                    fontSize: "14px",
                    padding: "8px 24px",
                    borderRadius: "8px",
                    fontWeight: 500,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#e68900", boxShadow: "0 4px 8px rgba(255, 149, 0, 0.2)" },
                    "&:disabled": { backgroundColor: "var(--fx-surface-muted, #F1F5F9)", color: "var(--fx-surface-texttertiary, #94A3B8)" },
                  }}
                >
                  {isSubmiting ? "Creating..." : "Use Template"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};
